import React, { useEffect, useState, useCallback, ReactNode } from 'react';
import { View } from 'react-native';
import { useSpringCarousel } from 'react-spring-carousel';
import { PlayableModel } from 'Types';
import Theme from 'Theme';
import styles from './CustomCarousel.jss';
import ThumbDot from '../Thumb/Thumb';

interface CustomCarouselProps {
    // testID?: string,
    onCarouselRef: (ref: {
        snapToPrev: () => void,
        snapToNext: () => void
    }) => void,
    data: PlayableModel[],
    renderItem: (itemProps: { item: PlayableModel, index: number }) => ReactNode,
    interval?: boolean,
    mlSeconds?: number,
    activeDot?: string,
    inactiveDot?: string
}

const CustomCarousel = ({
    onCarouselRef,
    data,
    renderItem,
    interval = false,
    mlSeconds = 1500,
    activeDot = Theme.primaryActiveColor,
    inactiveDot = Theme.lightThemeTextColor
}: CustomCarouselProps): React.ReactElement => {
    const [activeItem, setActiveItem] = useState<string>('0');
    const {
        carouselFragment,
        slideToPrevItem,
        slideToNextItem,
        slideToItem,
        useListenToCustomEvent,
    } = useSpringCarousel({
        withLoop: true,
        disableGestures: true,
        items: data.map((item, index) => ({
            id: String(index),
            renderItem: renderItem({ item, index }),
        })),
    });

    const Thumb = useCallback(({ item, index }) => (
        <ThumbDot
            color={activeItem === String(index) ? activeDot : inactiveDot}
            onPress={() => {
                slideToItem(index);
            }}
        />), [activeItem, slideToItem]);

    useListenToCustomEvent((event) => {
        if (event.eventName === 'onSlideStartChange') {
            setActiveItem(event.nextItem.id);
        }
    });

    useEffect(() => {
        const carouselRef = {
            snapToPrev: slideToPrevItem,
            snapToNext: slideToNextItem
        };
        onCarouselRef(carouselRef);
    }, []);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (interval) {
            timer = setInterval(() => {
                slideToNextItem();
            }, mlSeconds);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [slideToNextItem]);

    return (
        <View style={styles.container}>
            <View style={styles.track}>
                {carouselFragment}
                <View style={styles.thumbContainer}>
                    {data.map((item, index) =>
                        <Thumb key={item.getId()} item={item} index={index} />
                    )}
                </View>
            </View>
        </View>
    );
};


export default CustomCarousel;
